import ArrowLeftLong from '@ixigo/icons/long-arrow-left';
import { IconButton } from '@ixigo/iui';
import { cn } from '@ixigo/iui/variantStyle';
import { useRouter } from '@tanstack/react-router';
import { isForeignEnvironment, quit } from '@/lib/pwa';

// Glass back-press control for the CBCC route. Rendered ONLY inside a FOREIGN
// host webview, and only on mobile (the caller owns the device gate — see
// travel-credit-card-layout).
//
// Why it's conditional: ixiweb / iximweb are ordinary browsers and already have
// their own back affordance, so a second one is redundant noise. A FOREIGN client
// (the ixigo native app + partner PWAs — anything outside WEB_CLIENTS, see
// isForeignEnvironment) embeds this page in a chrome-less webview, so without
// this control the user has no way out of the page.
//
// Visually the SAME control as the /apply form header: circular iui IconButton
// with the long-arrow icon and the `ring-glass glass-soft` treatment, so the two
// pages match. The arrow is white here because every CBCC surface behind it is
// dark (the journey's video/photo scenes, the #0D0D0D flow sections), whereas
// /apply's mweb page is white and therefore uses text-primary.
//
// Placement mirrors the mweb wrapper's fixed logo row
// (travel-credit-card-mweb.tsx): same z-40 and the same `20px + --cbcc-banner-h`
// row origin, so the button sits on the logo's line at the left edge.
//
// It is CENTRED on the logo, not top-aligned with it — matching the /apply header,
// where the button is vertically centred in its flex row. Sharing the row's top
// edge would leave the two optically 6px apart, because this button is 44px tall
// (size="md") while the logo is 32px. So we target the logo's CENTRE line
// (row top + 16px = half the logo's height) and pull back by half the button's own
// height with -translate-y-1/2, which needs no button-height constant and stays
// correct if the size changes.
//
// The banner term is inherited from the layout wrapper and is 0 on mweb today (the
// top strip is web-only), but keeping it means the button can't ever overlap the
// banner if that changes.
export function CbccBackButton({ className }: { className?: string }) {
	const clientId = useRouter().options.context.clientConfig().clientId;
	// Ordinary web/mweb browsers keep their native back — render nothing.
	if (!isForeignEnvironment(clientId)) return null;

	// history.length > 1 ⇒ this webview has somewhere to go back TO, so a normal
	// back is right. When this page is the session's ONLY entry, back would
	// dead-end, so ask the HOST APP to close the webview instead. quit() bridges
	// to IxiWebView.quit / the iOS appInterface handler and no-ops (returns false)
	// when neither bridge exists, so this is safe even in a plain browser.
	const handleBack = () => {
		if (window.history.length > 1) window.history.back();
		else quit();
	};

	return (
		<IconButton
			size="md"
			aria-label="Go back"
			onClick={handleBack}
			className={cn(
				'fixed left-5 z-40 -translate-y-1/2 ring-glass glass-soft',
				// callers WITHOUT a server-resolved device gate pass `md:hidden` here
				// (the Manage Card preview route) — see the mobile-only note above.
				className,
			)}
			style={{ top: 'calc(20px + 16px + var(--cbcc-banner-h, 0px))' }}
		>
			<ArrowLeftLong className="text-white" />
		</IconButton>
	);
}
